import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import axios from 'axios';
import config from '../../config';
import dayjs from 'dayjs';

import Loading from '../general/loading.js';

import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';

import { green } from '@mui/material/colors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const EditParcelForm = ({ parcelId, property, onParcelUpdated, onClose }) => {
    const [formData, setFormData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchParcel = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/v2/parcel/${parcelId}`, {
                    headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
                });
                setFormData(response.data);
            } catch (error) {
                console.error('Error fetching parcel:', error);
                setError('Error fetching parcel. Please try again.');
            }
        };

        fetchParcel();
    }, [parcelId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const response = await axios.put(`${config.API_URL}/v2/parcel/${parcelId}`, formData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
            });

            if (response.status === 200 || response.status === 201) {
                console.log("Parcel updated.");
                onParcelUpdated(response.data);
                onClose();
                toast.success('Parcel updated successfully!');
            } else {
                toast.error('Failed to update the parcel.');
            }
        } catch (error) {
            console.error('Error updating parcel:', error);
            toast.error('Failed to update the parcel.');
        }
    };

    if (!formData) return <Loading />;

    return (
        <div className="popup-overlay">
            <div className="popup-content">
                <button className="close-popup" onClick={onClose}>
                    <i className="fas fa-times"></i>
                </button>
                <h2 className="text-2xl text-black">Edit Parcel</h2>
                <TextField
                    label="Carrier"
                    id="carrier"
                    name="carrier"
                    variant="outlined"
                    fullWidth
                    value={formData.carrier}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <TextField
                    label="Tracking Number"
                    id="trackingNumber"
                    name="trackingNumber"
                    variant="outlined"
                    fullWidth
                    value={formData.trackingNumber}
                    onChange={handleChange}
                    sx={{ marginBottom: '1rem' }}
                    required
                />
                <TextField
                    label="Description"
                    name="description"
                    multiline
                    fullWidth
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    sx={{ marginBottom: '1.2rem' }}
                    required
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Delivery Date"
                        value={formData.deliveryDate && dayjs(formData.deliveryDate).isValid() 
                            ? dayjs(formData.deliveryDate) 
                            : null}
                        onChange={(newValue) =>
                            setFormData((prevData) => ({
                                ...prevData,
                                deliveryDate: 
                                    newValue && dayjs(newValue).isValid()
                                    ? newValue.toISOString()
                                    : null,
                            }))
                        }
                        sx={{ width: '100%', marginBottom: '1rem' }}
                        required
                    />
                </LocalizationProvider>
                <Autocomplete
                    name="status"
                    value={formData.status || ''}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setFormData((prevData) => ({
                                ...prevData,
                                status: newValue,
                            }));
                        }
                    }}
                    options={['Pending', 'Delivered', 'Picked Up', 'Lost', 'Returned']}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Status"
                            variant="outlined"
                            fullWidth
                            required
                        />
                    )}
                    sx={{ marginBottom: '1rem' }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formData.notified}
                            onChange={(e) =>
                                setFormData((prevData) => ({
                                    ...prevData,
                                    notified: e.target.checked,
                                }))
                            }
                            sx={{
                                '&.Mui-checked': {
                                    color: green[600],
                                },
                            }}
                        />
                    }
                    label="Notified"
                    sx={{
                        fontSize: '14px',
                        '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                        },
                        marginBottom: '1rem',
                    }}
                />
                <button type="button" className="button" onClick={handleSubmit} >Update Parcel</button>
                {error && <div className="error-message">{error}</div>}
            </div>
        </div>
    );
};

export default EditParcelForm;
